import { SuretyAccountId } from "@inrev/common";
import {
	useAdminFetchAccountCompanies,
	useAdminFetchAccountIndividuals,
} from "@portal/api/account/admin";
import { Card, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import type { FC, ReactNode } from "react";
import { AdminAccountCompaniesCredit } from "./AdminAccountCompaniesCredit.tsx";
import { AdminAccountIndividualsCredit } from "./AdminAccountIndividualsCredit.tsx";

type AdminAccountCharacterSectionProps = {
	accountId: SuretyAccountId;
};

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

const CreditReportsCard = ({
	title,
	testId,
	children,
}: {
	title: string;
	testId: string;
	children: ReactNode;
}) => (
	<Card elevation="raised" className="rounded-xl" data-testid={testId}>
		<Card.Body className="flex w-full flex-col p-6 space-y-5">
			<Text type="title" color="primary">
				{title}
			</Text>
			{children}
		</Card.Body>
	</Card>
);

export const AdminAccountCharacterSection: FC<AdminAccountCharacterSectionProps> = ({
	accountId,
}) => {
	const { companies, companiesError, companiesIsFetched } =
		useAdminFetchAccountCompanies(accountId);
	const { individuals, individualsError, individualsIsFetched } =
		useAdminFetchAccountIndividuals(accountId);

	const characterReady = companiesIsFetched && individualsIsFetched;

	const companiesBody = companiesError ? (
		<ErrorDisplay error={toError(companiesError)} />
	) : (
		companies && (
			<AdminAccountCompaniesCredit accountId={accountId} companies={companies} hideHeading />
		)
	);

	const individualsBody = individualsError ? (
		<ErrorDisplay error={toError(individualsError)} />
	) : individuals && individuals.length > 0 ? (
		<AdminAccountIndividualsCredit accountId={accountId} individuals={individuals} hideHeading />
	) : null;

	return (
		<div data-testid="admin-account-character-section" className="flex flex-col space-y-5">
			<Text type="heading" color="primary">
				Character
			</Text>
			{!characterReady ? (
				<CenteredSpinner />
			) : (
				<>
					<CreditReportsCard
						title="Company Credit Reports"
						testId="admin-account-company-credit-card"
					>
						{companiesBody}
					</CreditReportsCard>
					{individualsBody && (
						<CreditReportsCard
							title="Individual Credit Reports"
							testId="admin-account-individual-credit-card"
						>
							{individualsBody}
						</CreditReportsCard>
					)}
				</>
			)}
		</div>
	);
};
